/**
 * CommandPalette · Jul 2026
 * ================================================================
 * ⌘K / Ctrl+K keyboard-first launcher for Operating-mode actions.
 * Keyboard nav (↑ / ↓ / Enter / Esc). Fuzzy substring search over
 * the same 6 core actions the OperatingCommandBar shows, plus a
 * few navigational fallbacks (Hub, Plan, Compliance, Budget).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Beaker,
  Activity,
  DollarSign,
  Bell,
  LayoutGrid,
  FileText,
  ShieldCheck,
  Command,
  X,
} from "lucide-react";

import { planLabel, planLink, projectsLabel } from "../../lib/planLabel";
import { useHubPrefs } from "../../lib/useHubPrefs";

export function buildDefaultActions(businessId, handlers = {}, business = null, hubPrefs = null) {
  const b = `/business/${businessId}`;
  return [
    // Feb 2027 · Weekly Review palette entry removed — the review is
    // now embedded on the Hub, so the palette shortcut was redundant.
    {
      id: "cohort-pnl",
      label: "Plan P&L",
      hint: "Month-by-month",
      icon: PieChart,
      color: "#2563EB",
      to: `${b}/cohorts`,
      keywords: ["cohort", "pnl", "profit", "loss", "trajectory"],
    },
    {
      id: "experiments",
      label: projectsLabel(business),
      hint: "What am I testing?",
      icon: Beaker,
      color: "#059669",
      to: `${b}/experiments`,
      keywords: ["experiments", "projects", "kanban", "test"],
    },
    {
      id: "log-snapshot",
      label: "Log a snapshot",
      hint: "Operating Health today",
      icon: Activity,
      color: "#4A4458",
      onSelect: () => handlers.openLog?.("snapshot"),
      keywords: ["log", "snapshot", "health", "score"],
    },
    {
      id: "log-revenue",
      label: "Log actuals",
      hint: "Monthly check-in (revenue + expenses)",
      icon: DollarSign,
      color: "#10B981",
      // Aug 2026 · Palette "Log actuals" mirrors the command bar
      // by navigating to the dedicated Monthly Check-In page.
      // Feb 2027 (revision) · Founders asked for every Log Actuals
      // link to land on the standalone `/log-actuals` page — NOT
      // the in-toolbar <LogThisMonthModal /> modal. So the palette
      // routes back to the standalone page via `to:`. `log-snapshot`
      // (Operating Health snapshot form) still opens the modal — it
      // is a different flow, not part of the "Log Actuals" surface.
      to: `${b}/log-actuals`,
      keywords: ["log", "actuals", "revenue", "expenses", "monthly"],
    },
    {
      id: "whats-due",
      label: "What's due",
      hint: "Compliance & deadlines",
      icon: Bell,
      color: "#F59E0B",
      to: `/compliance?business=${businessId}`,
      keywords: ["compliance", "due", "deadline", "filings"],
    },
    // Navigational fallbacks
    {
      id: "hub",
      label: "Business Hub",
      hint: "Overview",
      icon: LayoutGrid,
      color: "#4A4458",
      to: b,
      keywords: ["hub", "home", "overview"],
    },
    {
      id: "plan",
      label: planLabel(business),
      hint: "Sections + roadmap",
      icon: FileText,
      color: "#4A4458",
      to: planLink(business, business?.id, hubPrefs).replace(`/business/${business?.id}`, b),
      keywords: ["plan", "sections", "strategic", "business plan", "hierarchy"],
    },
    {
      id: "compliance",
      label: "Compliance",
      hint: "Filings & deadlines",
      icon: ShieldCheck,
      color: "#4A4458",
      to: `/compliance?business=${businessId}`,
      keywords: ["compliance", "legal", "filings"],
    },
  ];
}

function fuzzyMatch(action, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if (action.label.toLowerCase().includes(needle)) return true;
  if (action.hint && action.hint.toLowerCase().includes(needle)) return true;
  return (action.keywords || []).some((k) => k.toLowerCase().includes(needle));
}

export default function CommandPalette({ open, onClose, businessId, business, handlers }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { hubPrefs } = useHubPrefs();

  const actions = useMemo(
    () => buildDefaultActions(businessId, handlers || {}, business, hubPrefs),
    [businessId, handlers, business, hubPrefs],
  );
  const filtered = useMemo(() => actions.filter((a) => fuzzyMatch(a, q)), [actions, q]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
      // Focus after paint.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [q]);

  const run = useCallback(
    (a) => {
      if (!a) return;
      onClose?.();
      if (a.onSelect) {
        a.onSelect();
      } else if (a.to) {
        navigate(a.to);
      }
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        run(filtered[active]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, active, run, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="command-palette-backdrop"
    >
      <div
        className="mt-[10vh] w-[calc(100%-2rem)] max-w-lg bg-white border border-[#4A4458] shadow-[8px_8px_0px_rgba(74,68,88,1)]"
        style={{ borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="command-palette-modal"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200">
          <Command className="w-4 h-4 text-[#7C5FB0]" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a command… (weekly, log actuals, cohort)"
            className="flex-1 text-sm text-[#4A4458] placeholder:text-neutral-400 focus:outline-none"
            data-testid="command-palette-input"
            aria-label="Command palette input"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close command palette"
            className="p-1 text-neutral-500 hover:text-[#4A4458]"
            data-testid="command-palette-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul
          className="max-h-[50vh] overflow-y-auto"
          data-testid="command-palette-results"
          role="listbox"
        >
          {filtered.length === 0 && (
            <li
              className="px-4 py-6 text-center text-sm text-neutral-500"
              data-testid="command-palette-empty"
            >
              Nothing matches &quot;{q}&quot;. Try &quot;weekly&quot;, &quot;log&quot;, &quot;cohort&quot;.
            </li>
          )}
          {filtered.map((a, i) => {
            const Icon = a.icon;
            const activeRow = i === active;
            return (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => run(a)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeRow ? "bg-neutral-100" : "hover:bg-neutral-50"
                  }`}
                  data-testid={`command-palette-result-${a.id}`}
                  role="option"
                  aria-selected={activeRow}
                >
                  <span
                    className="w-8 h-8 flex items-center justify-center text-white"
                    style={{ background: a.color }}
                    aria-hidden
                  >
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-[#4A4458] truncate">
                      {a.label}
                    </span>
                    <span className="block text-[11px] text-neutral-500 truncate">
                      {a.hint}
                    </span>
                  </span>
                  {activeRow && (
                    <span
                      className="text-[10px] font-black uppercase tracking-[0.16em] text-[#7C5FB0]"
                      aria-hidden
                    >
                      ↵
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-neutral-200 bg-neutral-50 text-[10px] uppercase tracking-[0.16em] text-neutral-500">
          <span>↑ ↓ to navigate · Enter to select · Esc to close</span>
          <span className="hidden md:inline">
            <kbd className="px-1.5 py-0.5 border border-neutral-300 text-neutral-600 font-mono">
              ⌘K
            </kbd>
          </span>
        </div>
      </div>
    </div>
  );
}
